import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import viewAtoms from '@/shared/api/inventory/viewAtoms';
import useDataGridTable from '@/shared/components/dataDisplay/DataGridTable/hooks/useDataGridTable';
import useTableColumnVisibility from '@/shared/components/dataDisplay/DataGridTable/hooks/useTableColumnVisibility';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import { AtomModel_VIEW } from '@/shared/types/atom.type';
import MetadataDefinitions from '@/shared/types/metadata/metadataDefinitions.type';

import getInventoryItemsTableColumns from '../utils/getInventoryItemsTableColumns';

type Args = {
  metadataDefinitions: MetadataDefinitions[];
  inventoryId: string;
  refreshTableIndex?: number;
};

const useInventoryItemsTable_v2 = ({
  metadataDefinitions,
  inventoryId,
  refreshTableIndex,
}: Args) => {
  const redirectLogin = useRedirectLogin();

  const { apiRef, model, requestBody } = useDataGridTable();
  const requestBodyRef = useRef<typeof requestBody | null>(null);

  const [table, setTable] = useState({
    data: [] as AtomModel_VIEW[],
    totalRows: 0,
    isLoading: false,
  });

  const { tableColumnsList, tableRequiredColumns } =
    getInventoryItemsTableColumns(metadataDefinitions);

  const { columns, visibilityModel, setVisibilityModel } =
    useTableColumnVisibility({
      tableColumnsList: tableColumnsList,
      tableRequiredColumns: tableRequiredColumns,
    });

  useEffect(() => {
    const fetchInventoryItems = async () => {
      setTable((prev) => ({ ...prev, isLoading: true }));

      const searchArray = [
        {
          inventory_id: {
            cop: ['='],
            value: inventoryId,
          },
        },
        requestBody.search.length > 0
          ? requestBody.search.map((item, index) =>
              index === 0 ? { ...item, op: 'and' } : item,
            )
          : undefined,
      ];

      const initialRequest = {
        ...requestBody,
        search: searchArray.filter((item) => item !== undefined),
      };

      const response = await viewAtoms({
        body: initialRequest,
        inventoryId,
        onUnauthorized: redirectLogin,
      });

      setTable((prev) => ({ ...prev, isLoading: false }));

      if (response.success) {
        setTable((prev) => ({
          ...prev,
          data: response.data,
          totalRows: response.total_records || 0,
        }));
      }
    };

    if (!isEqual(requestBody, requestBodyRef.current) || refreshTableIndex) {
      requestBodyRef.current = requestBody;

      void fetchInventoryItems();
    }
  }, [refreshTableIndex, requestBody, redirectLogin, inventoryId]);

  return {
    apiRef,
    model,
    table,
    columns,
    visibilityModel,
    setVisibilityModel,
  };
};

export default useInventoryItemsTable_v2;
